import axios, { isAxiosError } from 'axios';
import { translate } from 'i18n/jhipster-language';
const isRecord = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
/** Skip noisy session/account probes — same heuristic as shahypay `notification-middleware`. */
export function shouldSuppressCmsApiErrorToast(error) {
    if (!isAxiosError(error) || error.response?.status !== 401) {
        return false;
    }
    const ae = error;
    const data = ae.response?.data;
    const path = isRecord(data) && typeof data.path === 'string'
        ? data.path
        : '';
    const emptyMsg = ae.message === '';
    return emptyMsg || path.includes('/api/account') || path.includes('/users/api/account');
}
const normalizeFieldViolationMessage = (msg) => ['Min', 'Max', 'DecimalMin', 'DecimalMax'].includes(msg) ? 'Size' : msg;
function headerStringValue(raw) {
    if (typeof raw === 'string') {
        return raw;
    }
    if (Array.isArray(raw)) {
        const first = raw[0];
        return typeof first === 'string' ? first : undefined;
    }
    return undefined;
}
/**
 * Mirrors host `notification-middleware` header handling (`*-app-error` / `*-app-params`).
 */
function readAppAlertFromHeaders(headers) {
    let errorKey = null;
    let entityKey = null;
    if (!headers || typeof headers !== 'object') {
        return { errorKey, entityKey };
    }
    const tryPair = (k, v) => {
        const low = k.toLowerCase();
        const val = headerStringValue(v);
        if (!val)
            return;
        if (low.endsWith('app-error')) {
            errorKey = val;
        }
        else if (low.endsWith('app-params')) {
            entityKey = decodeURIComponent(val.replace(/\+/g, ' '));
        }
    };
    const hAny = headers;
    if (typeof hAny.forEach === 'function') {
        hAny.forEach((v, k) => tryPair(String(k), v));
    }
    else {
        for (const k of Object.keys(hAny)) {
            tryPair(k, hAny[k]);
        }
    }
    return { errorKey, entityKey };
}
function translateFieldErrors(fieldErrors) {
    const lines = [];
    for (const fe of fieldErrors) {
        if (fe == null || typeof fe !== 'object')
            continue;
        const rawMsg = fe.message ?? '';
        const normalized = normalizeFieldViolationMessage(rawMsg);
        const convertedField = String(fe.field ?? '').replace(/\[\d*\]/g, '[]');
        const fieldName = `${fe.objectName ? `${fe.objectName}.` : ''}${convertedField}`.trim() || 'field';
        const tpl = translate(`error.${normalized}`, { fieldName });
        lines.push(tpl);
    }
    return lines.filter(Boolean).join('\n');
}
function translateProblemCode(data) {
    const props = data.properties;
    const code = props != null && typeof props === 'object' && typeof props.code === 'string'
        ? props.code
        : undefined;
    if (!code?.trim()) {
        return undefined;
    }
    const detail = typeof data.detail === 'string' ? data.detail.trim() : '';
    const params = typeof data.params === 'object' && data.params !== null ? data.params : undefined;
    const fromDetail = detail;
    const fromCode = translate(code.trim(), params);
    return fromDetail || fromCode;
}
/**
 * User-facing error line after `translate()`, matching Shahypay notification middleware semantics.
 */
export function resolveTranslatedCmsApiErrorDetail(error) {
    const networkFb = translate('error.server.not.reachable');
    if (!isAxiosError(error)) {
        if (error instanceof Error && error.message.trim()) {
            return error.message.trim();
        }
        if (typeof error === 'string' && error.trim()) {
            return translate(error.trim());
        }
        return networkFb;
    }
    const ae = error;
    const noResponseSkipCancel = !ae.response &&
        ae.config &&
        ae.code !== 'ERR_CANCELED' &&
        (typeof axios.isCancel !== 'function' || !axios.isCancel(ae));
    if (noResponseSkipCancel) {
        return networkFb;
    }
    const status = ae.response?.status ?? 0;
    const data = ae.response?.data;
    const asRecord = isRecord(data) ? data : undefined;
    if (status === 404) {
        return translate('error.url.not.found');
    }
    /** JH Problem + optional `message` translation key */
    const fromProblemCode = asRecord ? translateProblemCode(asRecord) : undefined;
    if (fromProblemCode) {
        return fromProblemCode;
    }
    if (status === 400 && ae.response?.headers && asRecord) {
        const { errorKey, entityKey } = readAppAlertFromHeaders(ae.response.headers);
        if (errorKey) {
            let entityLabel = '';
            if (entityKey) {
                entityLabel =
                    translate(`global.menu.entities.${entityKey}`) || entityKey.replace(/-/g, ' ');
            }
            return translate(errorKey, entityLabel ? { entityName: entityLabel } : {});
        }
        const fe = asRecord.fieldErrors;
        if (Array.isArray(fe) && fe.length > 0) {
            return translateFieldErrors(fe);
        }
        const msgKey = typeof asRecord.message === 'string' ? asRecord.message.trim() : '';
        if (msgKey) {
            const params = typeof asRecord.params === 'object' && asRecord.params !== null
                ? asRecord.params
                : undefined;
            return translate(msgKey, params);
        }
    }
    if (asRecord) {
        if (typeof asRecord.detail === 'string' && asRecord.detail.trim()) {
            return asRecord.detail.trim();
        }
        if (typeof asRecord.title === 'string' && asRecord.title.trim()) {
            return asRecord.title.trim();
        }
        if (typeof asRecord.message === 'string' && asRecord.message.trim()) {
            const mk = asRecord.message.trim();
            const params = typeof asRecord.params === 'object' && asRecord.params !== null
                ? asRecord.params
                : undefined;
            return translate(mk, params);
        }
    }
    if (ae.response != null && status > 0) {
        const dotted = translate(`error.http.${status}`);
        if (dotted !== `error.http.${status}`) {
            return dotted;
        }
        const text = typeof ae.response.statusText === 'string' ? ae.response.statusText.trim() : '';
        return text.trim() ? `HTTP ${status} ${text}` : `HTTP ${status}`;
    }
    if (ae.code === 'ECONNABORTED' || ae.message?.toLowerCase().includes('network')) {
        return networkFb;
    }
    if (ae.message?.trim()) {
        return ae.message.trim();
    }
    return networkFb;
}
/**
 * @deprecated Prefer {@link resolveTranslatedCmsApiErrorDetail} — it applies `translate()` / API keys consistently.
 */
export function getCmsApiErrorMessage(error, fallback) {
    try {
        return resolveTranslatedCmsApiErrorDetail(error);
    }
    catch {
        return fallback;
    }
}
